import type { IBondScreenerPresetConfig, TFilterPayload } from '@/creditflowresearch/domain/vo/Bondscreener';
import { DEFAULT_FILTERS } from '@/creditflowresearch/application/CorporateBondScreener/buildCorporateBondScreenerRequest';
import { EXCLUDE_FLAG_SOURCE_KEYS } from '@/creditflowresearch/application/CorporateBondScreener/excludeFlagKeys';
import { hasValue } from '@/creditflowresearch/application/BondScreener/filterValueHelpers';

type TFilterKey = keyof TFilterPayload;

const isExcludeFlagActive = (sourceKeys: TFilterKey[], filters: TFilterPayload): boolean =>
  sourceKeys.some((sourceKey) => hasValue(filters[sourceKey]));

const isDraftValueMatchingPreset = (key: TFilterKey, presetValue: unknown, draftFilters: TFilterPayload): boolean => {
  const excludeFlagSourceKeys = EXCLUDE_FLAG_SOURCE_KEYS[key as keyof typeof EXCLUDE_FLAG_SOURCE_KEYS];
  if (excludeFlagSourceKeys) {
    return isExcludeFlagActive(excludeFlagSourceKeys, draftFilters) === Boolean(presetValue);
  }

  const draftValue: unknown = draftFilters[key];
  if (Array.isArray(presetValue)) {
    return Array.isArray(draftValue)
      && presetValue.length === draftValue.length
      && presetValue.every((id) => draftValue.includes(id));
  }
  return presetValue === draftValue;
};

const TREE_SELECTION_RESET_RULES: Partial<Record<TFilterKey, TFilterKey[]>> = {
  selectedCountryIds: ['includedRegionIds', 'includedCountryIds'],
  selectedSubindustryIds: ['includedIndustryIds', 'includedSubindustryIds'],
};

const clearTreeSelections = (presetFilterKeys: TFilterKey[]): Partial<TFilterPayload> => {
  const touchedByPreset = new Set(presetFilterKeys);

  return (Object.entries(TREE_SELECTION_RESET_RULES) as [TFilterKey, TFilterKey[]][])
    .reduce<Partial<TFilterPayload>>((cleared, [treeSelectionKey, sourceKeys]) => {
      if (!sourceKeys.some((sourceKey) => touchedByPreset.has(sourceKey))) return cleared;
      return { ...cleared, [treeSelectionKey]: DEFAULT_FILTERS[treeSelectionKey] };
    }, {});
};

export const isPresetActive = (preset: IBondScreenerPresetConfig, draftFilters: TFilterPayload): boolean =>
  (Object.keys(preset.filters) as TFilterKey[]).every(
    (key) => isDraftValueMatchingPreset(key, preset.filters[key], draftFilters),
  );

export const applyPresetFilters = (preset: IBondScreenerPresetConfig, draftFilters: TFilterPayload): TFilterPayload => {
  const presetFilterKeys = Object.keys(preset.filters) as TFilterKey[];

  return {
    ...draftFilters,
    ...clearTreeSelections(presetFilterKeys),
    ...preset.filters,
  };
};

export const removePresetFilters = (
  preset: IBondScreenerPresetConfig,
  draftFilters: TFilterPayload,
): TFilterPayload => {
  const presetFilterKeys = Object.keys(preset.filters) as TFilterKey[];
  const restoredDefaults = presetFilterKeys.reduce<Partial<TFilterPayload>>((defaults, key) => ({
    ...defaults,
    [key]: DEFAULT_FILTERS[key],
  }), {});

  return { ...draftFilters, ...restoredDefaults, ...clearTreeSelections(presetFilterKeys) };
};
